/**
 * Global Error Boundary Component
 *
 * Catches errors in React component tree and displays fallback UI.
 * Integrates with error handling system for logging and reporting.
 *
 * @module GlobalErrorBoundary
 * @version 1.0.0
 * @date 2025-11-19
 * @related CQ-004 Error Handling Standardization
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AppError, ErrorCode, ErrorCategory } from '@/utils/errors/AppError';
import { handleError } from '@/utils/errors/errorHandler';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertTriangle, RefreshCw, Home, Bug } from 'lucide-react';
import { LanguageContextType } from '@/contexts/language/LanguageProvider';
import { LanguageContext } from '@/contexts/language/LanguageProvider';

// ============================================================================
// TYPES
// ============================================================================

interface Props {
  children: ReactNode;
  /** Fallback UI component */
  fallback?: (error: AppError, errorInfo: ErrorInfo, resetError: () => void) => ReactNode;
  /** Callback when error is caught */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** Should auto-reset on route change? */
  autoResetOnRouteChange?: boolean;
}

interface State {
  hasError: boolean;
  error: AppError | null;
  errorInfo: ErrorInfo | null;
}

// ============================================================================
// ERROR BOUNDARY COMPONENT
// ============================================================================

/**
 * Global error boundary that catches all React errors
 *
 * @example
 * ```tsx
 * <GlobalErrorBoundary>
 *   <App />
 * </GlobalErrorBoundary>
 * ```
 */
export class GlobalErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Convert error to AppError
    const appError = new AppError({
      code: ErrorCode.UNKNOWN_ERROR,
      message: error.message,
      category: ErrorCategory.PROGRAMMING,
      isOperational: false,
      cause: error,
      metadata: {
        component: 'GlobalErrorBoundary',
      },
    });

    return {
      hasError: true,
      error: appError,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Special handling for React error #31 (invalid element type - often chunk loading failure)
    if (error.message && error.message.includes('invariant=31')) {
      console.error('[GlobalErrorBoundary] React error #31 detected - likely chunk loading failure');

      // Log to Sentry with special tag
      handleError(error, {
        context: {
          component: 'GlobalErrorBoundary',
          componentStack: errorInfo.componentStack,
          errorType: 'chunk-loading-failure',
        },
        showToast: false,
        logError: true,
        reportToSentry: true,
      });

      // Clear service worker caches
      if ('caches' in window) {
        caches.keys().then(names => {
          console.log('[GlobalErrorBoundary] Clearing', names.length, 'cache(s) before reload');
          Promise.all(names.map(name => caches.delete(name)))
            .then(() => {
              console.log('[GlobalErrorBoundary] Caches cleared');
            })
            .catch(err => {
              console.error('[GlobalErrorBoundary] Failed to clear caches:', err);
            });
        });
      }

      // Update state to show error UI (with reload button)
      this.setState({ errorInfo });
      this.props.onError?.(error, errorInfo);
      return;
    }

    // Handle error with centralized handler
    handleError(error, {
      context: {
        component: 'GlobalErrorBoundary',
        componentStack: errorInfo.componentStack,
      },
      showToast: false, // Don't show toast as we're rendering error UI
      logError: true,
      reportToSentry: true,
    });

    // Update state with error info
    this.setState({
      errorInfo,
    });

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);
  }

  componentDidUpdate(prevProps: Props): void {
    // Auto-reset on children change (typically route change)
    if (this.props.autoResetOnRouteChange && this.props.children !== prevProps.children) {
      this.resetError();
    }
  }

  resetError = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  reloadPage = (): void => {
    window.location.reload();
  };

  goHome = (): void => {
    window.location.href = '/';
  };

  reportBug = (t: (key: any) => string): void => {
    // Open bug report form or email
    const subject = `${t('error.bugReportSubject')}: ${this.state.error?.code}`;
    const body = `
${t('error.message')}: ${this.state.error?.message}
${t('error.code')}: ${this.state.error?.code}
Timestamp: ${this.state.error?.timestamp}

Component Stack:
${this.state.errorInfo?.componentStack}
    `.trim();

    window.open(`mailto:contact@subvention360.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
  };

  render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.state.errorInfo!, this.resetError);
      }

      // Default error UI with translations
      return (
        <LanguageContext.Consumer>
          {(context) => {
            if (!context) {
              // Fallback if context is not available
              return this.renderDefaultErrorUI();
            }

            const { t } = context;

            return (
              <div className="min-h-screen bg-background flex items-center justify-center p-4">
                <Card className="max-w-2xl w-full">
                  <CardHeader>
                    <div className="flex items-center space-x-2">
                      <AlertTriangle className="h-6 w-6 text-destructive" />
                      <CardTitle>{t('error.title')}</CardTitle>
                    </div>
                    <CardDescription>
                      {t('error.description')}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="space-y-6">
                    {/* Error details */}
                    <Alert variant="destructive">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertTitle>{t('error.details')}</AlertTitle>
                      <AlertDescription className="mt-2 space-y-1">
                        <p><strong>{t('error.code')}:</strong> {this.state.error.code}</p>
                        <p><strong>{t('error.message')}:</strong> {this.state.error.message}</p>
                        {process.env.NODE_ENV === 'development' && (
                          <details className="mt-2">
                            <summary className="cursor-pointer hover:underline">{t('error.technicalDetails')}</summary>
                            <pre className="mt-2 text-xs overflow-auto p-2 bg-muted rounded">
                              {JSON.stringify(this.state.error.toJSON(), null, 2)}
                            </pre>
                            {this.state.errorInfo && (
                              <pre className="mt-2 text-xs overflow-auto p-2 bg-muted rounded max-h-40">
                                {this.state.errorInfo.componentStack}
                              </pre>
                            )}
                          </details>
                        )}
                      </AlertDescription>
                    </Alert>

                    {/* Recovery instructions */}
                    {this.state.error.recovery.userInstructions && (
                      <Alert>
                        <AlertDescription>
                          {this.state.error.recovery.userInstructions}
                        </AlertDescription>
                      </Alert>
                    )}

                    {/* Action buttons */}
                    <div className="flex flex-wrap gap-3">
                      <Button onClick={this.resetError} variant="default">
                        <RefreshCw className="mr-2 h-4 w-4" />
                        {t('error.tryAgain')}
                      </Button>

                      <Button onClick={this.reloadPage} variant="outline">
                        <RefreshCw className="mr-2 h-4 w-4" />
                        {t('error.reloadPage')}
                      </Button>

                      <Button onClick={this.goHome} variant="outline">
                        <Home className="mr-2 h-4 w-4" />
                        {t('error.goHome')}
                      </Button>

                      <Button onClick={() => this.reportBug(t)} variant="ghost">
                        <Bug className="mr-2 h-4 w-4" />
                        {t('error.reportBug')}
                      </Button>
                    </div>

                    {/* Support message */}
                    <div className="text-sm text-muted-foreground border-t pt-4">
                      <p>
                        {t('error.supportMessage')}{' '}
                        <a href="mailto:contact@subvention360.com" className="underline hover:text-foreground">
                          contact@subvention360.com
                        </a>
                      </p>
                      <p className="mt-1 text-xs">
                        {t('error.errorId')}: {this.state.error.timestamp.getTime().toString(36)}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            );
          }}
        </LanguageContext.Consumer>
      );
    }

    return this.props.children;
  }

  // Fallback method if LanguageContext is not available
  private renderDefaultErrorUI(): ReactNode {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-2xl w-full">
          <CardHeader>
            <div className="flex items-center space-x-2">
              <AlertTriangle className="h-6 w-6 text-destructive" />
              <CardTitle>Une erreur est survenue</CardTitle>
            </div>
            <CardDescription>
              Une erreur inattendue s'est produite. Nous avons été notifiés et travaillons sur une correction.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Détails de l'erreur</AlertTitle>
              <AlertDescription className="mt-2 space-y-1">
                <p><strong>Code:</strong> {this.state.error?.code}</p>
                <p><strong>Message:</strong> {this.state.error?.message}</p>
              </AlertDescription>
            </Alert>
            <div className="flex flex-wrap gap-3">
              <Button onClick={this.resetError} variant="default">
                <RefreshCw className="mr-2 h-4 w-4" />
                Réessayer
              </Button>
              <Button onClick={this.goHome} variant="outline">
                <Home className="mr-2 h-4 w-4" />
                Retour à l'Accueil
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
}

// ============================================================================
// ROUTE-LEVEL ERROR BOUNDARY
// ============================================================================

interface RouteErrorBoundaryProps {
  children: ReactNode;
  /** Route name for error tracking */
  routeName?: string;
  /** Fallback UI component */
  fallback?: (error: AppError, resetError: () => void) => ReactNode;
}

/**
 * Route-level error boundary for catching errors in specific routes
 *
 * @example
 * ```tsx
 * <RouteErrorBoundary routeName="Profile">
 *   <ProfilePage />
 * </RouteErrorBoundary>
 * ```
 */
export class RouteErrorBoundary extends Component<RouteErrorBoundaryProps, State> {
  constructor(props: RouteErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    const appError = new AppError({
      code: ErrorCode.UNKNOWN_ERROR,
      message: error.message,
      category: ErrorCategory.PROGRAMMING,
      isOperational: false,
      cause: error,
    });

    return {
      hasError: true,
      error: appError,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    handleError(error, {
      context: {
        route: this.props.routeName,
        componentStack: errorInfo.componentStack,
      },
      showToast: false,
      logError: true,
      reportToSentry: true,
    });

    this.setState({ errorInfo });
  }

  componentDidUpdate(prevProps: RouteErrorBoundaryProps): void {
    // Reset error when route changes
    if (this.props.children !== prevProps.children) {
      this.resetError();
    }
  }

  resetError = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.resetError);
      }

      // Default minimal error UI for routes with translations
      return (
        <LanguageContext.Consumer>
          {(context) => {
            if (!context) {
              // Fallback if context is not available
              return this.renderDefaultRouteErrorUI();
            }

            const { t } = context;

            return (
              <div className="flex items-center justify-center min-h-[400px] p-4">
                <Card className="max-w-md w-full">
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <AlertTriangle className="h-5 w-5 text-destructive" />
                      <span>{t('error.loadingPage')}</span>
                    </CardTitle>
                    <CardDescription>
                      {this.state.error.userMessage}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <Button onClick={this.resetError} className="w-full">
                        <RefreshCw className="mr-2 h-4 w-4" />
                        {t('error.tryAgain')}
                      </Button>
                      <Button onClick={() => window.history.back()} variant="outline" className="w-full">
                        {t('error.goBack')}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            );
          }}
        </LanguageContext.Consumer>
      );
    }

    return this.props.children;
  }

  // Fallback method if LanguageContext is not available
  private renderDefaultRouteErrorUI(): ReactNode {
    return (
      <div className="flex items-center justify-center min-h-[400px] p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              <span>Erreur de Chargement de la Page</span>
            </CardTitle>
            <CardDescription>
              {this.state.error?.userMessage}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <Button onClick={this.resetError} className="w-full">
                <RefreshCw className="mr-2 h-4 w-4" />
                Réessayer
              </Button>
              <Button onClick={() => window.history.back()} variant="outline" className="w-full">
                Retour
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
}

// ============================================================================
// EXPORT
// ============================================================================

export default GlobalErrorBoundary;
